import { useCallback, useMemo } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import DecisionFooter from '../../DecisionFooter.jsx';
import { useWorkQueueContext } from '../../../context/useWorkQueueContext.js';
import { hitlActionErrorToast } from '../../../utils/hitlActionToast.js';
import { isDecisionSkipped } from '../../../utils/decisionSkipped.js';
import { UserCheck, CheckCircle2, UserCog } from 'lucide-react';

/**
 * HTL Handoff decision: Acknowledge and take ownership → once the event is
 * owned by the current persona, "Mark resolved" replaces Acknowledge. Reassign
 * reuses the CT-4858 people picker owned by InboxSplitView.
 *
 * Ownership is derived from the event itself, so the footer stays correct when
 * the reviewer moves between events. Acknowledge/Reassign are local ownership
 * ops (no resolve); "Mark resolved" is the terminal decision → CT-5096 fires
 * POST /v1/tasks/hitl/resolve with `decision.status: approved`.
 */
function HandoffDecision({ event, actions }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const {
    currentPersona,
    updateEventOwner,
    appendAuditEntry,
    resolveHitlEvent,
    pendingDecisionIds,
  } = useWorkQueueContext();

  const acknowledged = event.owner?.isMine === true;
  const isResolving = pendingDecisionIds?.has(event.id) ?? false;
  const handleAcknowledge = useCallback(() => {
    updateEventOwner(event.id, {
      id: currentPersona.id,
      name: currentPersona.name,
      initials: currentPersona.initials,
      isMine: true,
    });
    appendAuditEntry(event.id, {
      id: `handoff-ack-${Date.now()}`,
      actor: currentPersona.name,
      action: t('handoff.auditAcknowledged'),
      timeAgo: t('team.justNow'),
    });
    toast.success({
      title: t('handoff.acknowledgedTitle'),
      message: t('handoff.acknowledgedMessage'),
    });
  }, [appendAuditEntry, currentPersona, event.id, t, toast, updateEventOwner]);

  const handleResolve = useCallback(async () => {
    try {
      await resolveHitlEvent(event.id, {
        status: 'approved',
        auditAction: t('handoff.auditResolved'),
      });
      toast.success({ title: t('handoff.resolvedTitle'), message: t('handoff.resolvedMessage') });
    } catch (err) {
      if (isDecisionSkipped(err)) return;
      const { title, message } = hitlActionErrorToast(t, err, 'handoff');
      toast.error({ title, message });
    }
  }, [resolveHitlEvent, event.id, t, toast]);

  const openReassign = actions.openReassign;
  const handleReassign = useCallback(() => {
    openReassign(event);
  }, [openReassign, event]);

  const primaryActions = useMemo(
    () => [
      acknowledged
        ? {
            id: 'resolve',
            label: isResolving ? t('hitl.resolving') : t('handoff.markResolved'),
            size: 'md',
            icon: CheckCircle2,
            disabled: isResolving,
            onClick: handleResolve,
          }
        : {
            id: 'ack',
            label: t('handoff.acknowledge'),
            size: 'md',
            icon: UserCheck,
            onClick: handleAcknowledge,
          },
      {
        id: 'reassign',
        label: t('team.reassign'),
        size: 'md',
        icon: UserCog,
        variant: 'secondary',
        disabled: isResolving,
        onClick: handleReassign,
      },
    ],
    [acknowledged, isResolving, handleAcknowledge, handleReassign, handleResolve, t]
  );

  return (
    <DecisionFooter
      event={event}
      primaryActions={primaryActions}
      onSkip={actions.skip}
      onAsk={actions.ask}
      onEscalate={actions.escalate}
      onTrace={actions.trace}
      extraMenuItems={actions.extraMenuItems}
      sticky={actions.sticky}
    />
  );
}

export default HandoffDecision;
